import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { getCoachDeletionPreviewAPI } from '../../api/employee';
import { CarryApiResult, CoachDeletionPreview } from '../../types/coachCarry';
import { carryErrorMessage } from '../../lib/coachCarry';
import CoachNotice from './CoachNotice';
import { carryDate } from '../../lib/coachCarryDisplay';
import { useAccessMode } from '../../context/AccessModeContext';
import { usePublicMemberList } from '../../features/publicMember/hooks';
import { ICenterMember } from '../../features/publicMember/types';

type Props = {
    centerId: string;
    coachId: string;
    accessToken: string;
    onClose: () => void;
    onCarry?: () => void;
    onBusyChange?: (busy: boolean) => void;
    onResultUnknown?: () => void;
    onRefreshTrainerList?: () => Promise<void>;
    onDelete: (convertMemberIds: string[]) => Promise<CarryApiResult<unknown>>;
};

type DeletionMember = {
    memberId: string;
    memberName: string;
    remainSession?: number;
    memberships?: ICenterMember['memberships'];
    hasGroupTicket?: boolean;
    groupTicketCount?: number;
    withoutPT?: boolean;
};

const CONVERT_MEMBER_LIMIT = 1000;
const CONVERT_LIMIT_MESSAGE = '한 번에 1,000명까지 처리할 수 있어요. 선택한 회원 수를 줄여 주세요';
const UNRESOLVED_CONVERT_MESSAGE = '이 방법으로는 정리되지 않아요. 수강권을 개별로 정리하거나 「센터 회원으로 바꾸고 삭제」로 진행해 주세요';
const UNKNOWN_DELETE_MESSAGE = '삭제 결과를 확인하지 못했어요. 강사 목록을 새로고침해 삭제됐는지 먼저 확인해 주세요';

const memberSummary = (member: DeletionMember) => {
    // 여러 PT는 첫 수강권만 표시한다. 회차를 역산하거나 날짜로 상태를 추정하지 않는다.
    const ticket = member.memberships?.[0];
    const remaining = ticket ? ticket.remainSession : member.remainSession;
    const expiration = carryDate(ticket?.expirationDate);
    const extra = (member.memberships?.length || 0) - 1;
    const pt = typeof remaining === 'number' ? `PT 남은 ${remaining}회 · ${expiration ? `${expiration} 만료` : '만료 정보 없음'}${extra > 0 ? ` · 외 ${extra}개` : ''}`
        : member.withoutPT ? 'PT 없어요' : 'PT 정보 없음';
    const group = typeof member.groupTicketCount === 'number' ? member.groupTicketCount > 0 ? `그룹권 ${member.groupTicketCount}개` : ''
        : typeof member.hasGroupTicket === 'boolean' ? member.hasGroupTicket ? '그룹권 있어요' : '' : '그룹권 정보 없음';
    return [pt, group].filter(Boolean).join(' · ');
};

// 부모의 단일 Modal 안에서 회원 선택과 마지막 확인 단계를 전환한다.
const ModalCoachDeleteMembers = ({ centerId, coachId, accessToken, onClose, onCarry, onBusyChange, onResultUnknown, onRefreshTrainerList, onDelete }: Props) => {
    const { isReadOnly } = useAccessMode();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [step, setStep] = useState<'members' | 'confirm'>('members');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [checked, setChecked] = useState(false);
    const [busy, setBusy] = useState(false);
    const submitting = useRef(false);
    const [unresolved, setUnresolved] = useState<string[]>([]);
    const [unresolvedConverted, setUnresolvedConverted] = useState<string[]>([]);
    const [resultUnknown, setResultUnknown] = useState(false);
    const knownMemberNames = useRef<Record<string, string>>({});
    const [error, setError] = useState('');
    const memberList = usePublicMemberList({
        centerId, coach: coachId, page: 1, limit: 5000, timeout: 60_000,
    });
    const preview = useQuery<CoachDeletionPreview>({
        queryKey: ['coachDeleteMembers', centerId, coachId],
        queryFn: async () => {
            const data = await getCoachDeletionPreviewAPI(centerId, coachId, accessToken);
            if (!Array.isArray(data?.withActivePT) || !Array.isArray(data?.withOnlyGroupTickets)
                || !Array.isArray(data?.withNoTickets)
                || [...data.withActivePT, ...data.withOnlyGroupTickets, ...data.withNoTickets]
                    .some((member) => typeof member?.memberId !== 'string' || !member.memberId)) {
                throw new Error('회원 정보를 불러오지 못했어요.');
            }
            return data;
        },
        // 앱 공용 QueryClient 기본값이 enabled:false 라 명시해야 조회가 시작된다(src/lib/queryClient.ts).
        enabled: !!centerId && !!coachId && !!accessToken,
        retry: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
    });
    useEffect(() => { setChecked(false); }, [preview.dataUpdatedAt, memberList.dataUpdatedAt]);
    useEffect(() => { document.getElementById('coach-delete-title')?.focus(); }, [step]);

    // deletion-preview에는 이어받음/인계 완료 값이 없다. 분류·누락만으로 완료를 추정하지 않는다.
    const byId = new Map<string, DeletionMember>((memberList.data?.results || []).map((member) => [member.memberId, {
        memberId: member.memberId, memberName: member.name,
        memberships: member.memberships?.filter((ticket) => ticket.coachId === coachId && ticket.status === 'active'),
        hasGroupTicket: member.hasGroupTicket, groupTicketCount: member.groupTicketCount,
    }]));
    for (const member of preview.data?.withActivePT || []) byId.set(member.memberId, { ...byId.get(member.memberId), ...member });
    for (const member of preview.data?.withOnlyGroupTickets || []) byId.set(member.memberId, { ...byId.get(member.memberId), ...member, withoutPT: true });
    for (const member of preview.data?.withNoTickets || []) byId.set(member.memberId, { ...byId.get(member.memberId), ...member, withoutPT: true });
    const members = Array.from(byId.values());
    members.forEach((member) => {
        if (member.memberName) knownMemberNames.current[member.memberId] = member.memberName;
    });
    const loading = memberList.isFetching || preview.isFetching;
    const incompleteList = !memberList.data || memberList.isError || memberList.isLoading
        || (memberList.data?.totalResults ?? 0) > (memberList.data?.results.length ?? 0);
    const exceedsLimit = selectedIds.length > CONVERT_MEMBER_LIMIT;
    const blocked = !preview.data || preview.isError || incompleteList || busy || loading || isReadOnly || resultUnknown;
    const allSelected = members.length > 0 && members.every((member) => selectedIds.includes(member.memberId));
    const activePTCount = new Set((preview.data?.withActivePT || []).filter((member) => selectedIds.includes(member.memberId)).map((member) => member.memberId)).size;
    const notListed = unresolved.filter((id) => !byId.has(id));
    const unnamedNotListed = notListed.filter((id) => !knownMemberNames.current[id]);
    const refreshTrainerList = async () => {
        if (submitting.current) return;
        submitting.current = true;
        setBusy(true);
        try {
            await onRefreshTrainerList?.();
        } finally {
            void queryClient.invalidateQueries({ queryKey: ['getTrainerListAPI'] });
            navigate('/trainerManage');
            submitting.current = false;
            setBusy(false);
        }
    };
    const submit = async () => {
        if (submitting.current || !checked || blocked || exceedsLimit) return;
        submitting.current = true;
        onBusyChange?.(true);
        setBusy(true);
        setError('');
        const submittedIds = [...selectedIds];
        try {
            const response = await onDelete(submittedIds);
            if (response.status === 200) return;
            if (response.status === 0) {
                setResultUnknown(true);
                onResultUnknown?.();
                setError(UNKNOWN_DELETE_MESSAGE);
            } else if (response.status === 409 && response.data?.errorCode === 'COACH_DELETE_MEMBERS_UNRESOLVED') {
                const memberIds = response.data.details?.memberIds;
                const ids = Array.isArray(memberIds) ? memberIds.filter((id) => typeof id === 'string' && id) : [];
                const converted = ids.filter((id) => submittedIds.includes(id));
                setUnresolved(ids);
                setUnresolvedConverted(converted);
                setError(converted.length > 0 && converted.length === ids.length
                    ? '전환을 선택했지만 처리가 남은 회원을 표시했어요. 회원별 안내를 확인해 주세요.'
                    : '처리가 남은 회원을 표시했어요. 이어받기·인계를 마치거나 센터 회원으로 전환해 주세요.');
                setStep('members');
            } else {
                setError(carryErrorMessage(response.status, response.data));
            }
        } catch {
            setResultUnknown(true);
            onResultUnknown?.();
            setError(UNKNOWN_DELETE_MESSAGE);
        } finally {
            setChecked(false);
            submitting.current = false;
            onBusyChange?.(false);
            setBusy(false);
        }
    };
    const primaryClass = 'min-h-[44px] rounded-[8px] px-[24px] py-[12px] text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40';

    return (
        <>
            <p className="mb-[8px] text-[13px] text-LAVEL_2">강사 삭제 · {step === 'members' ? '2단계' : '3단계'}</p>
            <h2 id="coach-delete-title" tabIndex={-1} className="mb-[16px] text-[22px] font-bold leading-[32px] focus:outline-none">{step === 'members' ? '센터 회원으로 바꿀 회원을 골라 주세요' : '강사 삭제 전 확인해 주세요'}</h2>
            <div className="min-h-0 overflow-y-auto">
                {loading && <p role="status" className="mb-[16px] text-LAVEL_2">회원 정보를 불러오고 있어요.</p>}
                {memberList.isError && <p role="alert" className="mb-[12px] text-ERROR">전체 회원 목록을 불러오지 못했어요. <button className="underline" onClick={() => memberList.refetch()}>다시 불러오기</button></p>}
                {memberList.data && incompleteList && !memberList.isLoading && <p role="alert" className="mb-[12px] text-ERROR">회원 목록을 모두 확인할 수 없어요. 「센터 회원으로 바꾸고 삭제」를 선택하거나 수강권을 개별로 정리해 주세요.</p>}
                {preview.isError && <p role="alert" className="mb-[12px] text-ERROR">회원 정보를 불러오지 못했어요. <button className="underline" onClick={() => preview.refetch()}>다시 불러오기</button></p>}
                {error && <p role="alert" className="mb-[16px] text-ERROR"><CoachNotice message={error} /></p>}
                {exceedsLimit && <p role="alert" className="mb-[16px] text-ERROR"><CoachNotice message={CONVERT_LIMIT_MESSAGE} /></p>}
                {step === 'members' ? <>
                    <p className="mb-[12px] text-[13px] text-LAVEL_2">고른 회원은 PT가 만료되고 센터 회원으로 남아요. 다른 센터로 데려갈 회원은 먼저 이어받기 해 주세요.</p>
                    {onCarry && <button type="button" disabled={busy || isReadOnly || resultUnknown} onClick={onCarry} className="mb-[20px] text-[13px] text-primary underline">먼저 이어받기 하기</button>}
                    <div className="mb-[4px] flex items-center justify-between border-b border-solid border-GREY_50 pb-[12px]">
                        <div className="flex min-h-[44px] items-center gap-[8px]">
                            <input id="coach-delete-select-all" type="checkbox" className="h-[20px] w-[20px] shrink-0 cursor-pointer accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed" checked={allSelected} disabled={blocked || members.length === 0}
                                onChange={(event) => { setChecked(false); setSelectedIds(event.target.checked ? members.map((member) => member.memberId) : []); }} />
                            <label htmlFor="coach-delete-select-all" className="flex min-h-[44px] cursor-pointer items-center pr-[12px]">전체 선택</label>
                        </div>
                        <span aria-live="polite" className="rounded-full bg-BLUE_50 px-[10px] py-[4px] text-[13px] font-bold text-primary">{selectedIds.length}명 선택</span>
                    </div>
                    <div>
                        {members.map((member) => <label key={member.memberId} className={`flex cursor-pointer items-center justify-between gap-[16px] border-b border-solid border-GREY_50 px-[8px] py-[16px] last:border-b-0 ${unresolved.includes(member.memberId) ? 'rounded-[8px] bg-ERROR_50' : ''}`}>
                            <span className="min-w-0 flex-1">
                                <span className="flex flex-wrap items-center gap-[8px]"><strong>{member.memberName || '이름 없는 회원'}</strong>
                                    {unresolved.includes(member.memberId) && <span className="rounded-full bg-ERROR_50 px-[10px] py-[4px] text-[13px] font-bold text-ERROR">{unresolvedConverted.includes(member.memberId) ? '개별 정리 필요' : '처리 필요'}</span>}
                                </span>
                                <span title={memberSummary(member)} className="mt-[4px] block break-words text-[13px] text-LAVEL_2">{memberSummary(member)}</span>
                                {unresolvedConverted.includes(member.memberId) && <span className="mt-[8px] block text-[13px] text-ERROR"><CoachNotice message={UNRESOLVED_CONVERT_MESSAGE} /></span>}
                            </span>
                            <span className="flex shrink-0 items-center gap-[8px] text-[13px]">
                                <input type="checkbox" aria-label={`${member.memberName || '이름 없는 회원'} 센터 회원으로 전환`} className="h-[16px] w-[16px] accent-primary" disabled={blocked} checked={selectedIds.includes(member.memberId)}
                                    onChange={(event) => { setChecked(false); setSelectedIds(event.target.checked ? [...selectedIds, member.memberId] : selectedIds.filter((id) => id !== member.memberId)); }} />
                                센터 회원으로 전환
                            </span>
                        </label>)}
                        {preview.data && !loading && members.length === 0 && <p className="py-[20px] text-LAVEL_2">전환할 회원이 없어요. 남은 처리가 있는지는 삭제할 때 확인해요.</p>}
                        {unnamedNotListed.length > 0 && <p className="mt-[12px] text-ERROR">목록에 없는 회원 {unnamedNotListed.length.toLocaleString('ko-KR')}명</p>}
                        {notListed.map((id, index) => <div key={id} className="mt-[8px] rounded-[8px] bg-ERROR_50 p-[12px]">
                            <Link className="text-ERROR underline" aria-label={knownMemberNames.current[id] ? undefined : `목록에 없는 회원 ${index + 1} 정보 보기`} to={`/member/${id}`} state={{ memberId: id }}>{knownMemberNames.current[id] ? `${knownMemberNames.current[id]} · ` : ''}회원 정보 보기</Link>
                            <p className="mt-[8px] text-[13px]">{unresolvedConverted.includes(id) ? UNRESOLVED_CONVERT_MESSAGE : '회원 정보에서 수강권과 담당 강사를 확인해 주세요.'}</p>
                        </div>)}
                    </div>
                </> : <>
                    <p className="mb-[20px] text-LAVEL_2">선택한 {selectedIds.length}명은 센터 회원으로 전환하고 강사를 삭제해요.</p>
                    <div className="mb-[20px] rounded-[8px] bg-ERROR_50 p-[16px]">
                        <p className="font-bold">{activePTCount}명의 PT 수강권이 만료되고 예정 수업이 취소돼요</p>
                        <p className="mt-[4px] text-[13px] text-LAVEL_2">삭제 영향 미리보기 기준이에요. 그룹 수강권은 유지돼요.</p>
                    </div>
                    <details className="mb-[20px] text-[13px] text-LAVEL_2">
                        <summary className="cursor-pointer">선택한 회원 {selectedIds.length}명 보기</summary>
                        <ul className="mt-[8px] space-y-[8px]">{members.filter((member) => selectedIds.includes(member.memberId)).map((member) => <li key={member.memberId}>{member.memberName}</li>)}</ul>
                    </details>
                    <label className="flex items-center gap-[8px]">
                        <input type="checkbox" className="h-[16px] w-[16px] accent-primary" checked={checked} disabled={blocked || exceedsLimit} onChange={(event) => setChecked(event.target.checked)} />
                        선택한 회원의 수강권 만료·수업 취소와 강사 삭제를 확인했어요
                    </label>
                </>}
            </div>
            <div className="mt-[24px] flex shrink-0 justify-end gap-[12px]">
                <button type="button" disabled={busy || resultUnknown} onClick={() => { if (!submitting.current && !resultUnknown) { setChecked(false); if (step === 'confirm') setStep('members'); else onClose(); } }} className="min-h-[44px] rounded-[8px] bg-GREY px-[24px] py-[12px] text-[15px] font-bold disabled:opacity-40">돌아가기</button>
                {resultUnknown ? <button type="button" disabled={busy} onClick={refreshTrainerList} className={`${primaryClass} bg-primary`}>강사 목록 새로고침</button>
                    : step === 'members' ? <button type="button" onClick={() => { if (blocked || exceedsLimit) return; setChecked(false); setStep('confirm'); }} disabled={blocked || exceedsLimit} className={`${primaryClass} bg-primary`}>다음</button>
                        : <button type="button" onClick={submit} disabled={!checked || blocked || exceedsLimit} className={`${primaryClass} bg-ERROR`}>{busy ? '삭제하고 있어요' : '강사 삭제'}</button>}
            </div>
        </>
    );
};

export default ModalCoachDeleteMembers;
